import { Alert, Stack } from "@mui/material";
import { DataRow } from "Components/Shared/DataRow/DataRow";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { FormSection } from "Components/Shared/FormSection";
import { EditableSectionHeader } from "Components/UserAccount/Shared/EditableSectionHeader";
import { VatNumberForm } from "Components/UserAccount/Shared/Party/VatNumberForm";
import type { PartyDto } from "Infrastructure/Api/Api";
import {
  IS_VAT_NUMBER_FROM_REGISTRY,
  NO_VAT_NUMBER,
} from "Schemas/vatNumberSchema";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";

const RESOURCES = Resources.Components.Account;

type Props = {
  party: PartyDto;
};

// What the last save sent, and the party's verification stamp as of then — the refetched party carries
// a newer stamp, which is how "the save has landed" is told apart from the stale party still on screen.
type LastSave = {
  sentVatNumber: string;
  verifiedAtUtcBefore: string | null | undefined;
};

// The register is the source of truth: a cleared number that the DIČ implies comes back once the register
// confirms it. True only once the refetched party is on screen (stamp moved) and holds a different number
// than the one sent. (A scoped vat-number endpoint returning the verified number would make this a plain
// comparison — the whole-party PUT returns nothing, hence the stamp.)
const hasRegistryRestoredVatNumber = (
  { vatNumber, vatVerifiedAtUtc }: PartyDto,
  lastSave: LastSave | null,
): boolean =>
  lastSave !== null &&
  vatVerifiedAtUtc !== lastSave.verifiedAtUtcBefore &&
  vatNumber !== NO_VAT_NUMBER &&
  vatNumber !== lastSave.sentVatNumber;

/**
 * Lets a business party keep its own VAT registration current — registering for VAT (or ceasing to
 * be registered) is a normal event, and the number decides the tax regime of every document issued
 * to them afterwards.
 */
export const VatNumberSection = ({ party }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [lastSave, setLastSave] = useState<LastSave | null>(null);
  const { t } = useResource();
  const { region, vatNumber, vatStatus, vatVerifiedAtUtc, publicID } = party;

  const handleEditClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleSaved = (sentVatNumber: string) => {
    setLastSave({ sentVatNumber, verifiedAtUtcBefore: vatVerifiedAtUtc });
    setIsDialogOpen(false);
  };

  // Without a word about it, the user would take the reappeared number for a bug.
  const isVatNumberRestoredByRegistry = hasRegistryRestoredVatNumber(
    party,
    lastSave,
  );

  // Only a market where the client supplies the number gets an editable section: where a register
  // publishes it (CZ) the number equals the DIČ already shown in the company section above, and the
  // register — not the client — is the source of truth. An unknown region is left to admin.
  if (!region || IS_VAT_NUMBER_FROM_REGISTRY[region]) {
    return null;
  }

  return (
    <>
      <FormSection>
        <EditableSectionHeader
          title={t(RESOURCES.VatRegistration)}
          onEditClick={handleEditClick}
          isEditingDisabled={!publicID}
        />
        <Stack gap={2}>
          {/* Empty renders as a dash, not as "not registered" — the row below says what the register
              said (or that nobody has asked it yet, e.g. a company added through the add-role wizard). */}
          <DataRow
            label={t(Resources.Common.VatNumber)}
            value={vatNumber || null}
          />
          <DataRow
            label={t(Resources.Common.VatRegistryStatusLabel)}
            value={t(Resources.Common.VatStatus[vatStatus])}
          />
          {isVatNumberRestoredByRegistry && (
            <Alert severity="info">
              {t(RESOURCES.VatNumberRestoredByRegistry, { vatNumber })}
            </Alert>
          )}
        </Stack>
      </FormSection>
      <ModalDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        title={t(RESOURCES.VatRegistration)}
      >
        <VatNumberForm party={party} region={region} onSuccess={handleSaved} />
      </ModalDialog>
    </>
  );
};
